import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft, FileText, Upload, Trash, LockKey, Globe, Siren,
    CheckCircle, MapPin, PawPrint, Heart, ShareNetwork, WhatsappLogo,
    Copy, Sparkle, WarningOctagon, Certificate, Pill, Shield, Airplane,
    ShieldWarning, House, X,
} from "@phosphor-icons/react";
import PageShell from "@/components/PageShell";
import HealthHub from "@/components/health/HealthHub";
import api, { fileUrl } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useIsStoreApp } from "@/lib/storeAppDetection";
import { useHandlePremiumGate } from "@/lib/premiumGate";
import { finderUrl } from "@/lib/petUtils";
import { track } from "@/lib/analytics";

/* ---------- Personality library (grouped, color-coded) ---------- */
const PERSONALITY_GROUPS = [
    {
        key: "friendly", tone: "emerald",
        titleKey: "safety.personality.g_friendly",
        subKey: "safety.personality.g_friendly_sub",
        tags: [
            { key: "friendly_people", emoji: "🐶", labelKey: "safety.p.friendly_people" },
            { key: "friendly_dogs", emoji: "🐕", labelKey: "safety.p.friendly_dogs" },
            { key: "friendly_cats", emoji: "🐈", labelKey: "safety.p.friendly_cats" },
            { key: "good_children", emoji: "👶", labelKey: "safety.p.good_children" },
            { key: "trained", emoji: "🎓", labelKey: "safety.p.trained" },
            { key: "energetic", emoji: "🎾", labelKey: "safety.p.energetic" },
        ],
    },
    {
        key: "careful", tone: "amber",
        titleKey: "safety.personality.g_careful",
        subKey: "safety.personality.g_careful_sub",
        tags: [
            { key: "nervous", emoji: "😨", labelKey: "safety.p.nervous" },
            { key: "needs_space", emoji: "🚫", labelKey: "safety.p.needs_space" },
            { key: "do_not_approach", emoji: "⚠️", labelKey: "safety.p.do_not_approach" },
        ],
    },
    {
        key: "medical", tone: "sky",
        titleKey: "safety.personality.g_medical",
        subKey: "safety.personality.g_medical_sub",
        tags: [
            { key: "medical_needs", emoji: "💊", labelKey: "safety.p.medical_needs" },
            { key: "special_diet", emoji: "🥣", labelKey: "safety.p.special_diet" },
            { key: "needs_medication", emoji: "💉", labelKey: "safety.p.needs_medication" },
        ],
    },
];

const TONE_STYLES = {
    emerald: {
        chip: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20",
        active: "bg-emerald-500 text-white border-emerald-500 shadow-hover",
        icon: "text-emerald-600 dark:text-emerald-400",
    },
    amber: {
        chip: "bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-500/20",
        active: "bg-amber-500 text-white border-amber-500 shadow-hover",
        icon: "text-amber-600 dark:text-amber-400",
    },
    sky: {
        chip: "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/20",
        active: "bg-sky-500 text-white border-sky-500 shadow-hover",
        icon: "text-sky-600 dark:text-sky-400",
    },
};

/* ---------- Passport document categories ---------- */
const DOC_GROUPS = [
    { key: "health", titleKey: "safety.passport.g_health", icon: <Pill size={18} weight="fill" />, kinds: ["vaccine", "rabies", "medical"], tone: "text-rose-500" },
    { key: "vet", titleKey: "safety.passport.g_vet", icon: <Certificate size={18} weight="fill" />, kinds: ["vet"], tone: "text-emerald-500" },
    { key: "insurance", titleKey: "safety.passport.g_insurance", icon: <Shield size={18} weight="fill" />, kinds: ["insurance"], tone: "text-sky-500" },
    { key: "travel", titleKey: "safety.passport.g_travel", icon: <Airplane size={18} weight="fill" />, kinds: ["travel", "other"], tone: "text-indigo-500" },
];

const DOC_KINDS = [
    { key: "vaccine", labelKey: "safety.dockind.vaccine", emoji: "💉", tone: "from-rose-500 to-pink-500" },
    { key: "rabies", labelKey: "safety.dockind.rabies", emoji: "🦠", tone: "from-red-500 to-orange-500" },
    { key: "vet", labelKey: "safety.dockind.vet", emoji: "🩺", tone: "from-emerald-500 to-teal-500" },
    { key: "medical", labelKey: "safety.dockind.medical", emoji: "❤️‍🩹", tone: "from-pink-500 to-fuchsia-500" },
    { key: "insurance", labelKey: "safety.dockind.insurance", emoji: "🛡️", tone: "from-sky-500 to-blue-500" },
    { key: "travel", labelKey: "safety.dockind.travel", emoji: "✈️", tone: "from-indigo-500 to-violet-500" },
    { key: "other", labelKey: "safety.dockind.other", emoji: "📄", tone: "from-slate-500 to-zinc-500" },
];

/* ================ MAIN PAGE ================ */
export default function PetSafetyPage() {
    const { id } = useParams();
    const nav = useNavigate();
    const { t } = useI18n();
    const [pet, setPet] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("emergency");

    const load = async () => {
        try { const { data } = await api.get(`/pets/${id}`); setPet(data); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [id]);

    if (loading) return <PageShell testId="safety-loading"><p className="text-paw-ink2">{t("common.loading")}</p></PageShell>;
    if (!pet) return <PageShell testId="safety-error"><p>{t("safety.pet_not_found")}</p></PageShell>;

    const TABS = [
        { key: "emergency", labelKey: "safety.tab_emergency", icon: <Siren size={16} weight="fill" /> },
        { key: "personality", labelKey: "safety.tab_personality", icon: <Heart size={16} weight="fill" /> },
        { key: "passport", labelKey: "safety.tab_health", icon: <FileText size={16} weight="fill" /> },
    ];

    return (
        <PageShell testId="safety-page" maxWidth="max-w-3xl"
            kicker={t("safety.kicker")}
            title={t("safety.title", { name: pet.name })}
            sub={t("safety.sub", { name: pet.name })}
        >
            <Link to="/pets" data-testid="safety-back"
                className="inline-flex items-center gap-1.5 text-sm text-paw-ink2 dark:text-paw-bone/60 hover:text-paw-ink dark:hover:text-paw-bone transition -mt-8 mb-6">
                <ArrowLeft size={14} weight="bold" /> {t("safety.back_to_pets")}
            </Link>

            {/* Sticky tab bar */}
            <div className="sticky top-16 z-10 -mx-4 sm:mx-0 px-4 sm:px-0 pb-3 pt-1 bg-paw-bone/85 dark:bg-[hsl(152,15%,8%)]/85 backdrop-blur">
                <div className="flex gap-1 overflow-x-auto rounded-full border border-paw-line dark:border-white/10 bg-white dark:bg-paw-ink p-1" data-testid="safety-tabs">
                    {TABS.map((T) => {
                        const active = T.key === tab;
                        return (
                            <button key={T.key} type="button"
                                data-testid={`safety-tab-${T.key}`}
                                aria-selected={active}
                                onClick={() => setTab(T.key)}
                                className={`flex-1 min-w-0 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full text-sm font-medium transition ${active
                                    ? "bg-paw-forest text-white dark:bg-paw-terra"
                                    : "text-paw-ink2 dark:text-paw-bone/60 hover:text-paw-ink dark:hover:text-paw-bone"}`}
                            >
                                <span className={active ? "text-white" : "text-paw-forest dark:text-paw-terra"}>{T.icon}</span>
                                <span className="truncate">{t(T.labelKey)}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {tab === "emergency" && <EmergencyPanel pet={pet} onChange={load} />}
            {tab === "personality" && <PersonalityPanel pet={pet} onChange={load} />}
            {tab === "passport" && <HealthHub pet={pet} onChange={load} />}
        </PageShell>
    );
}

/* ============================================================
   EMERGENCY (LOST MODE) — one-tap SOS + live active dashboard
   ============================================================ */
function EmergencyPanel({ pet, onChange }) {
    const { t } = useI18n();
    const { user } = useAuth();
    const isStoreApp = useIsStoreApp();
    // Fail-closed: while AuthContext is still resolving `user`, we treat
    // the caller as a free user so the SOS button cannot leak into the
    // real activate-lost-mode form. Once user loads, real Premium users
    // pass through immediately.
    const authLoading = user === null;
    const isFreeUser = authLoading || user?.plan !== "premium";
    const openPremiumGate = useHandlePremiumGate(user);
    const isLost = Boolean(pet.is_lost);
    const [confirmOpen, setConfirmOpen] = useState(false);

    // Freemium gate:
    //   • Free store-app user → "Premium is coming soon" modal.
    //   • Free web user       → redirected to /subscription (Paddle).
    //   • Auth-loading user   → treated as gated (fail-closed).
    //   • Real Premium user   → passes through to the activate form.
    const handleArm = () => {
        if (isFreeUser) {
            openPremiumGate("lost_mode");
            return;
        }
        setConfirmOpen(true);
    };

    return (
        <section data-testid="safety-emergency" className="mt-6">
            {isLost
                ? <LostActiveCard pet={pet} onChange={onChange} />
                : <LostSOSCard pet={pet} onArm={handleArm} showPremiumBadge={isFreeUser && isStoreApp} />}

            {confirmOpen && (
                <ActivateLostModal pet={pet} onClose={() => setConfirmOpen(false)} onDone={onChange} />
            )}
        </section>
    );
}

function LostSOSCard({ pet, onArm, showPremiumBadge = false }) {
    const { t } = useI18n();
    return (
        <div className="space-y-5">
            {/* SOS Hero card */}
            <div
                data-testid="safety-lost-sos"
                className="relative rounded-3xl overflow-hidden bg-white dark:bg-[hsl(152,12%,10%)] border border-paw-line dark:border-white/10 shadow-hover"
            >
                {/* Pet hero photo */}
                <div className="relative h-40 sm:h-52 overflow-hidden">
                    {pet.photo_path
                        ? <img src={fileUrl(pet.photo_path)} alt={pet.name} className="pet-photo w-full h-full object-cover" />
                        : <div className="w-full h-full bg-paw-muted dark:bg-white/5 grid place-items-center text-paw-forest dark:text-paw-terra"><PawPrint size={56} weight="duotone" /></div>}
                    <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute top-3 start-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 dark:bg-paw-ink/85 backdrop-blur px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold text-paw-forest dark:text-paw-terra">
                        <Shield size={11} weight="fill" /> {t("safety.hero_safe")}
                    </div>
                    {showPremiumBadge && (
                        <div
                            data-testid="safety-lost-sos-premium-badge"
                            className="absolute top-3 end-3 inline-flex items-center gap-1 rounded-full bg-paw-terra text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 shadow-hover"
                        >
                            <LockKey size={10} weight="fill" /> {t("premium_soon.chip")}
                        </div>
                    )}
                    <div className="absolute bottom-0 inset-x-0 p-5">
                        <p className="text-white/80 text-xs uppercase tracking-widest font-semibold">{t("safety.sos_kicker")}</p>
                        <h2 className="font-display text-white text-2xl sm:text-3xl leading-tight mt-0.5">
                            {t("safety.sos_h", { name: pet.name })}
                        </h2>
                    </div>
                </div>

                <div className="p-5 sm:p-6">
                    <p className="text-sm text-paw-ink2 dark:text-paw-bone/70">
                        {t("safety.sos_sub", { name: pet.name })}
                    </p>

                    {/* Giant SOS button */}
                    <button
                        type="button"
                        data-testid="safety-sos-btn"
                        onClick={onArm}
                        className="relative mt-5 w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-b from-red-500 to-red-700 text-white font-bold px-6 py-5 text-base sm:text-lg hover:from-red-600 hover:to-red-800 transition shadow-hover overflow-hidden group"
                    >
                        <span aria-hidden className="absolute inset-0 opacity-40 group-hover:opacity-60 transition"
                              style={{ background: "radial-gradient(ellipse at top, rgba(255,255,255,0.35), transparent 60%)" }} />
                        <Siren size={22} weight="fill" className="relative" />
                        <span className="relative">{t("safety.sos_cta", { name: pet.name })}</span>
                    </button>

                    <p className="mt-3 text-[11px] text-center text-paw-ink2/70 dark:text-paw-bone/50">
                        {t("safety.sos_reassure", { name: pet.name })}
                    </p>
                </div>
            </div>

            {/* Three-step visualizer */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="safety-sos-steps">
                {[
                    { icon: <Siren size={18} weight="fill" />, key: "s1" },
                    { icon: <MapPin size={18} weight="fill" />, key: "s2" },
                    { icon: <House size={18} weight="fill" />, key: "s3" },
                ].map((s, i) => (
                    <div key={s.key} className="card-soft p-4">
                        <div className="flex items-center gap-2 text-paw-forest dark:text-paw-terra">
                            <span className="text-[10px] font-bold uppercase tracking-widest opacity-70">0{i + 1}</span>
                            {s.icon}
                        </div>
                        <p className="mt-2 text-sm font-semibold text-paw-ink dark:text-paw-bone">{t(`safety.step_${s.key}_h`, { name: pet.name })}</p>
                        <p className="mt-1 text-xs text-paw-ink2 dark:text-paw-bone/60 leading-relaxed">{t(`safety.step_${s.key}_d`, { name: pet.name })}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ActivateLostModal({ pet, onClose, onDone }) {
    const { t } = useI18n();
    const [lastSeen, setLastSeen] = useState("");
    const [instructions, setInstructions] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [gpsStatus, setGpsStatus] = useState("idle"); // idle | sharing | ok | off
    const [coords, setCoords] = useState(null);

    useEffect(() => {
        if (!navigator.geolocation) { setGpsStatus("off"); return; }
        setGpsStatus("sharing");
        navigator.geolocation.getCurrentPosition(
            (pos) => { setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setGpsStatus("ok"); },
            () => setGpsStatus("off"),
            { timeout: 6000 }
        );
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setError("");
        try {
            await api.post(`/pets/${pet.id}/lost`, {
                last_seen_location: lastSeen || null,
                instructions: instructions || null,
                last_seen_latitude: coords?.lat ?? null,
                last_seen_longitude: coords?.lng ?? null,
            });
            track("lost_mode_activated", {
                pet_id: pet.id,
                public_id: pet.public_id,
                has_gps: !!(coords?.lat && coords?.lng),
                has_last_seen: !!lastSeen,
            });
            onDone(); onClose();
        } catch (_) { setError(t("safety.activate_error")); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-paw-ink/50 backdrop-blur-sm grid place-items-end sm:place-items-center px-4 pb-4 sm:pb-0" data-testid="safety-activate-modal">
            <form onSubmit={submit} className="card-soft w-full max-w-md p-6 space-y-4 overflow-y-auto max-h-[92vh]">
                <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-red-600 text-white grid place-items-center flex-shrink-0"><Siren size={20} weight="fill" /></div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-display text-xl text-paw-ink dark:text-paw-bone">{t("safety.modal_h", { name: pet.name })}</h3>
                        <p className="mt-0.5 text-xs text-paw-ink2 dark:text-paw-bone/60">{t("safety.modal_sub")}</p>
                    </div>
                    <button type="button" onClick={onClose} className="text-paw-ink2 dark:text-paw-bone/60 hover:text-paw-ink dark:hover:text-paw-bone p-1"><X size={18} /></button>
                </div>

                <div>
                    <label className="label-soft">{t("safety.f_last_seen")}</label>
                    <input data-testid="safety-lost-location" className="input-soft mt-1.5" value={lastSeen} onChange={(e) => setLastSeen(e.target.value)} placeholder={t("safety.f_last_seen_ph")} />
                    <p className="mt-1.5 text-[11px] text-paw-ink2 dark:text-paw-bone/50 flex items-center gap-1">
                        <MapPin size={11} weight="fill" />
                        {gpsStatus === "sharing" && t("safety.gps_sharing")}
                        {gpsStatus === "ok" && <span className="text-emerald-600 dark:text-emerald-400">{t("safety.gps_ok")}</span>}
                        {gpsStatus === "off" && <span>{t("safety.gps_off")}</span>}
                        {gpsStatus === "idle" && t("safety.gps_idle")}
                    </p>
                </div>
                <div>
                    <label className="label-soft">{t("safety.f_instructions")}</label>
                    <textarea data-testid="safety-lost-instructions" rows={3} className="input-soft mt-1.5" value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder={t("safety.f_instructions_ph", { name: pet.name })} />
                </div>

                {error && <p className="text-sm text-red-600" data-testid="safety-activate-error">{error}</p>}

                <div className="flex gap-2 pt-1">
                    <button type="button" onClick={onClose} data-testid="safety-activate-cancel" className="btn-outline text-sm flex-1">{t("common.cancel")}</button>
                    <button type="submit" disabled={busy} data-testid="safety-activate-confirm"
                        className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-red-600 text-white font-bold px-5 py-3 text-sm hover:bg-red-700 transition disabled:opacity-60">
                        <Siren size={16} weight="fill" /> {busy ? t("safety.activating") : t("safety.activate_now")}
                    </button>
                </div>
                <p className="text-[11px] text-center text-paw-ink2/70 dark:text-paw-bone/50">{t("safety.activate_fineprint", { name: pet.name })}</p>
            </form>
        </div>
    );
}

function LostActiveCard({ pet, onChange }) {
    const { t } = useI18n();
    const { user } = useAuth();
    const authLoading = user === null;
    const isFreeUser = authLoading || user?.plan !== "premium";
    const openPremiumGate = useHandlePremiumGate(user);
    const [busy, setBusy] = useState(false);
    const [scanCount, setScanCount] = useState(null);
    const [copyOk, setCopyOk] = useState(false);
    // Rescue-alert state — fans a notification out to all opt-in nearby Guardians.
    const [alertRadius, setAlertRadius] = useState(3);
    const [alertBusy, setAlertBusy] = useState(false);
    const [alertResult, setAlertResult] = useState(null); // { fanned_out, radius_km } | null
    const [alertError, setAlertError] = useState("");
    const publicUrl = finderUrl(pet.public_id);

    // Live-ish timer
    const [now, setNow] = useState(Date.now());
    useEffect(() => { const id = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(id); }, []);
    const missingSince = pet.lost_since ? new Date(pet.lost_since) : null;
    const elapsed = missingSince ? formatElapsed(now - missingSince.getTime(), t) : "";

    // Scan count since lost
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/pets/${pet.id}/scans`);
                if (!pet.lost_since) { setScanCount(data.length); return; }
                const since = new Date(pet.lost_since).getTime();
                setScanCount(data.filter((s) => new Date(s.created_at || s.createdAt || 0).getTime() >= since).length);
            } catch { setScanCount(0); }
        })();
    }, [pet.id, pet.lost_since, now]);

    const markSafe = async () => {
        if (!window.confirm(t("safety.confirm_safe", { name: pet.name }))) return;
        setBusy(true);
        try { await api.post(`/pets/${pet.id}/found-safe`); onChange(); }
        finally { setBusy(false); }
    };

    const copyLink = async () => {
        try { await navigator.clipboard.writeText(publicUrl); setCopyOk(true); setTimeout(() => setCopyOk(false), 1500); } catch (_) { /* clipboard blocked */ }
    };

    const shareText = t("safety.share_msg", { name: pet.name, url: publicUrl });

    const sendRescueAlert = async () => {
        // Premium gate (fail-closed): rescue-alert broadcast is a Premium
        // recovery feature. Free users on store-app → coming-soon modal;
        // free web users → /subscription. Not-yet-loaded → gated.
        if (isFreeUser) {
            openPremiumGate("rescue_alert");
            return;
        }
        setAlertBusy(true); setAlertError(""); setAlertResult(null);
        try {
            const { data } = await api.post(`/pets/${pet.id}/rescue-alert`, { radius_km: alertRadius });
            setAlertResult(data);
        } catch (e) {
            setAlertError(e.response?.data?.detail || t("safety.rescue_alert_error"));
        } finally { setAlertBusy(false); }
    };

    return (
        <div className="space-y-5">
            {/* Emergency dashboard */}
            <div
                data-testid="safety-lost-active"
                className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-red-600 via-red-600 to-red-800 text-white shadow-hover"
            >
                {/* Ambient pulse */}
                <div aria-hidden className="absolute -top-24 -end-16 w-72 h-72 rounded-full bg-white/10 blur-3xl animate-pulse" />
                <div aria-hidden className="absolute -bottom-24 -start-16 w-72 h-72 rounded-full bg-yellow-300/10 blur-3xl" />

                <div className="relative p-6 sm:p-8">
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <span className="absolute inset-0 rounded-full bg-white/40 animate-ping" />
                            <span className="relative block w-2.5 h-2.5 rounded-full bg-white" />
                        </div>
                        <span className="text-[11px] uppercase tracking-[0.2em] font-bold">{t("safety.status_lost")}</span>
                    </div>

                    <div className="mt-4 flex items-center gap-4">
                        <div className="w-16 h-16 rounded-2xl overflow-hidden ring-2 ring-white/40 flex-shrink-0 bg-white/10">
                            {pet.photo_path
                                ? <img src={fileUrl(pet.photo_path)} alt={pet.name} className="pet-photo w-full h-full object-cover" />
                                : <div className="w-full h-full grid place-items-center"><PawPrint size={28} weight="duotone" /></div>}
                        </div>
                        <div className="min-w-0 flex-1">
                            <h2 className="font-display text-3xl sm:text-4xl leading-tight" data-testid="safety-lost-name">{pet.name}</h2>
                            <p className="text-sm text-white/80" data-testid="safety-lost-since">{elapsed ? t("safety.missing_for", { time: elapsed }) : t("safety.missing_now")}</p>
                        </div>
                    </div>

                    {/* Live stats */}
                    <div className="mt-5 grid grid-cols-2 gap-3">
                        <div className="rounded-2xl bg-white/12 backdrop-blur p-3">
                            <p className="text-[10px] uppercase tracking-widest text-white/70 font-semibold">{t("safety.stat_scans")}</p>
                            <p className="mt-1 font-display text-2xl" data-testid="safety-lost-scans">{scanCount === null ? "…" : scanCount}</p>
                        </div>
                        <div className="rounded-2xl bg-white/12 backdrop-blur p-3">
                            <p className="text-[10px] uppercase tracking-widest text-white/70 font-semibold">{t("safety.stat_qr")}</p>
                            <p className="mt-1 font-semibold text-sm flex items-center gap-1.5"><Siren size={14} weight="fill" /> {t("safety.stat_qr_on")}</p>
                        </div>
                    </div>

                    {/* Share row */}
                    <div className="mt-5">
                        <p className="text-[11px] uppercase tracking-widest text-white/80 font-semibold">{t("safety.share_h")}</p>
                        <div className="mt-2 grid grid-cols-3 gap-2">
                            <a href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noreferrer"
                                data-testid="safety-share-whatsapp"
                                className="flex items-center justify-center gap-1.5 rounded-full bg-white text-red-600 font-semibold px-3 py-2.5 text-xs hover:bg-white/95 transition">
                                <WhatsappLogo size={14} weight="fill" /> WhatsApp
                            </a>
                            <a href={`sms:?body=${encodeURIComponent(shareText)}`}
                                data-testid="safety-share-sms"
                                className="flex items-center justify-center gap-1.5 rounded-full bg-white/15 backdrop-blur border border-white/25 text-white font-semibold px-3 py-2.5 text-xs hover:bg-white/25 transition">
                                <ShareNetwork size={14} weight="fill" /> SMS
                            </a>
                            <button type="button" onClick={copyLink}
                                data-testid="safety-share-copy"
                                className="flex items-center justify-center gap-1.5 rounded-full bg-white/15 backdrop-blur border border-white/25 text-white font-semibold px-3 py-2.5 text-xs hover:bg-white/25 transition">
                                <Copy size={14} weight="fill" /> {copyOk ? t("safety.copied") : t("safety.copy_link")}
                            </button>
                        </div>
                    </div>

                    {/* Mark as safe */}
                    <button
                        type="button"
                        onClick={markSafe}
                        disabled={busy}
                        data-testid="safety-mark-found"
                        className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-white text-red-700 font-bold px-6 py-4 text-base hover:bg-white/95 transition disabled:opacity-60 shadow-hover"
                    >
                        <CheckCircle size={20} weight="fill" /> {t("safety.mark_safe_cta", { name: pet.name })}
                    </button>
                </div>
            </div>

            {/* Rescue-alert card — fan out to nearby opt-in Guardians */}
            <div
                className="card-soft p-4 sm:p-5"
                data-testid="safety-rescue-alert-card"
            >
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-red-500/12 text-red-600 grid place-items-center flex-shrink-0">
                        <Siren size={18} weight="fill" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="font-display text-lg text-paw-ink dark:text-paw-bone">{t("safety.rescue_alert_h")}</p>
                        <p className="mt-0.5 text-[12px] text-paw-ink2 dark:text-paw-bone/60">{t("safety.rescue_alert_sub")}</p>
                    </div>
                </div>

                <div className="mt-3 flex items-center gap-1.5 flex-wrap" data-testid="safety-rescue-alert-radius">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-paw-ink2 dark:text-paw-bone/60 me-1">{t("community.radius_h")}:</span>
                    {[1, 3, 5, 10].map((r) => (
                        <button
                            key={r} type="button" onClick={() => setAlertRadius(r)}
                            data-testid={`safety-rescue-radius-${r}`}
                            aria-pressed={alertRadius === r}
                            className={`px-3 py-1 rounded-full text-xs font-semibold transition ${
                                alertRadius === r
                                    ? "bg-red-600 text-white"
                                    : "bg-white dark:bg-white/5 border border-paw-line dark:border-white/10 text-paw-ink2 dark:text-paw-bone/70 hover:bg-paw-muted dark:hover:bg-white/10"
                            }`}
                        >{r} km</button>
                    ))}
                </div>

                <button
                    type="button" onClick={sendRescueAlert}
                    disabled={alertBusy}
                    data-testid="safety-rescue-alert-send"
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-br from-red-500 to-red-700 hover:brightness-110 disabled:opacity-70 text-white font-bold px-5 py-3 text-sm shadow-soft transition"
                >
                    <Siren size={16} weight="fill" />
                    {alertBusy
                        ? t("safety.rescue_alert_sending")
                        : t("safety.rescue_alert_send", { r: alertRadius })}
                </button>

                {alertResult && (
                    <p data-testid="safety-rescue-alert-result" className="mt-2 text-[12px] text-emerald-700 dark:text-emerald-300">
                        ✅ {t("safety.rescue_alert_result", { n: alertResult.fanned_out, r: alertResult.radius_km })}
                    </p>
                )}
                {alertError && (
                    <p data-testid="safety-rescue-alert-error" className="mt-2 text-[12px] text-red-600 dark:text-red-400">{alertError}</p>
                )}
                <p className="mt-2 text-[10px] text-paw-ink2/70 dark:text-paw-bone/40">
                    🔒 {t("safety.rescue_alert_privacy")}
                </p>
            </div>

            {/* What the finder sees preview */}
            <div className="card-soft p-4 sm:p-5" data-testid="safety-lost-preview">
                <p className="text-[11px] uppercase tracking-widest text-paw-ink2 dark:text-paw-bone/60 font-semibold">{t("safety.preview_h")}</p>
                <div className="mt-3 flex items-start gap-3 rounded-2xl border border-red-200 dark:border-red-800/40 bg-red-50/50 dark:bg-red-950/20 p-3">
                    <ShieldWarning size={20} weight="fill" className="text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-800 dark:text-red-200">
                        <p className="font-semibold">{t("safety.preview_line1", { name: pet.name })}</p>
                        <p className="mt-0.5 text-red-700/90 dark:text-red-300/90">{t("safety.preview_line2")}</p>
                    </div>
                </div>
                <a href={publicUrl} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-paw-forest dark:text-paw-terra hover:underline">
                    {t("safety.preview_cta")} <ShareNetwork size={12} weight="fill" />
                </a>
            </div>
        </div>
    );
}

function formatElapsed(ms, t) {
    if (!ms || ms < 0) return "";
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60), h = Math.floor(m / 60), d = Math.floor(h / 24);
    if (d >= 1) return t("safety.elapsed_d", { d });
    if (h >= 1) return t("safety.elapsed_h", { h });
    if (m >= 1) return t("safety.elapsed_m", { m });
    return t("safety.elapsed_now");
}

/* ============================================================
   PERSONALITY — 3 color-coded groups, big tappable badges
   ============================================================ */
function PersonalityPanel({ pet, onChange }) {
    const { t } = useI18n();
    const [selected, setSelected] = useState(new Set(pet.personality || []));
    const [busy, setBusy] = useState(false);
    const [savedFlash, setSavedFlash] = useState(false);
    const dirty = useMemo(() => {
        const cur = pet.personality || [];
        if (cur.length !== selected.size) return true;
        return cur.some((k) => !selected.has(k));
    }, [pet.personality, selected]);

    const toggle = (k) => {
        const next = new Set(selected);
        if (next.has(k)) next.delete(k); else next.add(k);
        setSelected(next);
    };

    const save = async () => {
        setBusy(true);
        try {
            const payload = { ...pet, personality: Array.from(selected) };
            ["id", "user_id", "created_at", "updated_at", "public_id", "is_lost",
             "lost_since", "lost_reward", "lost_last_seen_location", "lost_last_seen_latitude",
             "lost_last_seen_longitude", "lost_instructions", "documents"]
             .forEach((k) => delete payload[k]);
            await api.put(`/pets/${pet.id}`, payload);
            onChange();
            setSavedFlash(true);
            setTimeout(() => setSavedFlash(false), 1800);
        } finally { setBusy(false); }
    };

    return (
        <section data-testid="safety-personality" className="mt-6 space-y-5">
            <div className="card-soft p-5 sm:p-6">
                <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-paw-forest/10 dark:bg-paw-terra/15 text-paw-forest dark:text-paw-terra grid place-items-center flex-shrink-0">
                        <Sparkle size={20} weight="fill" />
                    </div>
                    <div className="flex-1">
                        <h2 className="font-display text-xl sm:text-2xl text-paw-ink dark:text-paw-bone">{t("safety.personality.h", { name: pet.name })}</h2>
                        <p className="mt-1 text-sm text-paw-ink2 dark:text-paw-bone/60">{t("safety.personality.sub", { name: pet.name })}</p>
                    </div>
                </div>
            </div>

            {PERSONALITY_GROUPS.map((group) => {
                const tone = TONE_STYLES[group.tone];
                return (
                    <div key={group.key} data-testid={`safety-personality-group-${group.key}`}>
                        <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] uppercase tracking-widest font-bold ${tone.chip}`}>
                            {t(group.titleKey)}
                        </div>
                        <p className="mt-2 text-xs text-paw-ink2 dark:text-paw-bone/60">{t(group.subKey, { name: pet.name })}</p>
                        <div className="mt-3 grid grid-cols-2 gap-2.5">
                            {group.tags.map((tag) => {
                                const active = selected.has(tag.key);
                                return (
                                    <button key={tag.key} type="button"
                                        data-testid={`safety-personality-${tag.key}`}
                                        aria-selected={active}
                                        onClick={() => toggle(tag.key)}
                                        className={`flex items-center gap-2.5 rounded-2xl border-2 p-3 text-start transition ${active
                                            ? tone.active
                                            : "bg-white dark:bg-white/5 border-paw-line dark:border-white/10 text-paw-ink dark:text-paw-bone hover:border-paw-line/70 dark:hover:border-white/20"}`}
                                    >
                                        <span className="text-xl leading-none">{tag.emoji}</span>
                                        <span className="text-sm font-medium leading-tight">{t(tag.labelKey)}</span>
                                        {active && <CheckCircle size={14} weight="fill" className="ms-auto flex-shrink-0" />}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );
            })}

            {/* Sticky save bar */}
            <div className="sticky bottom-4 z-10 flex justify-end">
                <button type="button" disabled={busy || !dirty}
                    data-testid="safety-personality-save"
                    onClick={save}
                    className={`inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold shadow-hover transition ${dirty
                        ? "bg-paw-forest text-white dark:bg-paw-terra hover:opacity-95"
                        : "bg-white dark:bg-white/10 text-paw-ink2 dark:text-paw-bone/60 border border-paw-line dark:border-white/10"}`}
                >
                    {savedFlash
                        ? <><CheckCircle size={16} weight="fill" /> {t("safety.saved")}</>
                        : <>{busy ? t("common.saving") : dirty ? t("safety.personality.save_cta") : t("safety.personality.no_changes")}</>}
                </button>
            </div>
        </section>
    );
}

/* ============================================================
   DIGITAL PET PASSPORT — wallet-style cards
   ============================================================ */
function PassportPanel({ pet, onChange }) {
    const { t } = useI18n();
    const documents = pet.documents || [];

    return (
        <section data-testid="safety-passport" className="mt-6 space-y-5">
            {/* Passport cover card */}
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-paw-forest via-paw-forest to-paw-forestDark text-white shadow-hover" data-testid="safety-passport-cover">
                <div aria-hidden className="absolute -top-16 -end-10 w-56 h-56 rounded-full bg-paw-terra/25 blur-3xl" />
                <div aria-hidden className="absolute -bottom-20 -start-10 w-64 h-64 rounded-full bg-white/8 blur-3xl" />
                <div className="relative p-6 sm:p-8 flex items-center gap-4">
                    <div className="w-20 h-20 rounded-2xl overflow-hidden ring-2 ring-white/40 flex-shrink-0 bg-white/10">
                        {pet.photo_path
                            ? <img src={fileUrl(pet.photo_path)} alt={pet.name} className="pet-photo w-full h-full object-cover" />
                            : <div className="w-full h-full grid place-items-center"><PawPrint size={32} weight="duotone" /></div>}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] uppercase tracking-[0.25em] text-paw-terra/95 font-bold">{t("safety.passport.brand")}</p>
                        <h2 className="font-display text-2xl sm:text-3xl leading-tight mt-0.5">{pet.name}</h2>
                        <p className="text-xs text-white/70 mt-1">ID · {(pet.public_id || "").slice(0, 8).toUpperCase()}</p>
                    </div>
                    <Certificate size={28} weight="fill" className="text-paw-terra flex-shrink-0 hidden sm:block" />
                </div>
            </div>

            <UploadCard pet={pet} onChange={onChange} />

            {documents.length === 0 ? (
                <div className="card-soft p-8 text-center" data-testid="safety-doc-empty">
                    <div className="inline-grid place-items-center w-14 h-14 rounded-2xl bg-paw-forest/10 dark:bg-paw-terra/12 text-paw-forest dark:text-paw-terra mb-3">
                        <FileText size={26} weight="duotone" />
                    </div>
                    <p className="font-display text-lg text-paw-ink dark:text-paw-bone">{t("safety.passport.empty_h")}</p>
                    <p className="text-sm text-paw-ink2 dark:text-paw-bone/60 mt-1">{t("safety.passport.empty_sub", { name: pet.name })}</p>
                </div>
            ) : (
                <div className="space-y-5" data-testid="safety-doc-groups">
                    {DOC_GROUPS.map((group) => {
                        const docs = documents.filter((d) => group.kinds.includes(d.kind || "other"));
                        if (docs.length === 0) return null;
                        return (
                            <div key={group.key} data-testid={`safety-doc-group-${group.key}`}>
                                <div className="flex items-center gap-2 mb-2.5 px-1">
                                    <span className={group.tone}>{group.icon}</span>
                                    <h3 className="text-[11px] uppercase tracking-widest font-bold text-paw-ink dark:text-paw-bone">{t(group.titleKey)}</h3>
                                </div>
                                <div className="space-y-2.5">
                                    {docs.map((d) => (
                                        <DocumentCard key={d.id} pet={pet} doc={d} onChange={onChange} />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </section>
    );
}

function UploadCard({ pet, onChange }) {
    const { t } = useI18n();
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const [name, setName] = useState("");
    const [kind, setKind] = useState("vaccine");
    const [isPublic, setIsPublic] = useState(false);

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setUploading(true); setError("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("name", name || file.name);
            fd.append("kind", kind);
            fd.append("is_public", String(isPublic));
            // NOTE: do NOT set Content-Type manually — the browser must add the
            // multipart boundary itself. Setting it here strips the boundary and
            // the request comes through malformed (Cloudflare then returns 502).
            await api.post(`/pets/${pet.id}/documents`, fd);
            setName("");
            onChange();
        } catch (err) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === "string" ? detail : t("safety.upload_error"));
        } finally { setUploading(false); }
    };

    return (
        <div className="card-soft p-5 sm:p-6" data-testid="safety-doc-upload-card">
            <h3 className="font-display text-lg text-paw-ink dark:text-paw-bone">{t("safety.passport.add_h")}</h3>
            <p className="mt-1 text-sm text-paw-ink2 dark:text-paw-bone/60">{t("safety.passport.add_sub")}</p>

            <div className="mt-4 space-y-3">
                <div>
                    <label className="label-soft">{t("safety.f_doc_name")}</label>
                    <input data-testid="safety-doc-name" className="input-soft mt-1.5" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("safety.f_doc_name_ph")} />
                </div>
                <div>
                    <label className="label-soft">{t("safety.f_doc_kind")}</label>
                    <select data-testid="safety-doc-kind" className="input-soft mt-1.5" value={kind} onChange={(e) => setKind(e.target.value)}>
                        {DOC_KINDS.map((k) => <option key={k.key} value={k.key}>{k.emoji}  {t(k.labelKey)}</option>)}
                    </select>
                </div>
                <label className="flex items-center gap-3 rounded-2xl border border-paw-line dark:border-white/10 p-3 cursor-pointer">
                    <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} data-testid="safety-doc-public-toggle" className="w-4 h-4" />
                    <div className="flex-1">
                        <p className="text-sm font-medium text-paw-ink dark:text-paw-bone flex items-center gap-1.5">
                            {isPublic ? <Globe size={14} weight="fill" className="text-paw-forest dark:text-paw-terra" /> : <LockKey size={14} weight="fill" />}
                            {t(isPublic ? "safety.public_on" : "safety.public_off")}
                        </p>
                        <p className="text-[11px] text-paw-ink2 dark:text-paw-bone/60">{t(isPublic ? "safety.public_on_hint" : "safety.public_off_hint")}</p>
                    </div>
                </label>
                <label
                    className={`btn-primary text-sm cursor-pointer justify-center w-full relative overflow-hidden transition-opacity ${uploading ? "opacity-90 cursor-wait" : ""}`}
                    data-testid="safety-doc-upload"
                    aria-busy={uploading}
                >
                    {uploading ? (
                        <>
                            <span aria-hidden className="relative w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                            <span className="ms-1.5">{t("safety.uploading")}</span>
                        </>
                    ) : (
                        <>
                            <Upload size={16} weight="fill" />
                            <span className="ms-1.5">{t("safety.passport.upload_cta")}</span>
                        </>
                    )}
                    <input
                        type="file"
                        accept="image/*,application/pdf"
                        hidden
                        onChange={onFile}
                        disabled={uploading}
                        data-testid="safety-doc-file-input"
                    />
                </label>
                {error && <p className="text-sm text-red-600" data-testid="safety-doc-error">{error}</p>}
            </div>
        </div>
    );
}

function DocumentCard({ pet, doc, onChange }) {
    const { t } = useI18n();
    const kind = DOC_KINDS.find((k) => k.key === doc.kind) || DOC_KINDS[6];
    const [busy, setBusy] = useState(false);

    const togglePublic = async () => {
        setBusy(true);
        try { await api.patch(`/pets/${pet.id}/documents/${doc.id}`, { is_public: !doc.is_public }); onChange(); }
        finally { setBusy(false); }
    };
    const remove = async () => {
        if (!window.confirm(t("safety.confirm_remove_doc", { name: doc.name }))) return;
        setBusy(true);
        try { await api.delete(`/pets/${pet.id}/documents/${doc.id}`); onChange(); }
        finally { setBusy(false); }
    };

    return (
        <article
            data-testid={`safety-doc-${doc.id}`}
            className="relative overflow-hidden rounded-2xl border border-paw-line dark:border-white/10 bg-white dark:bg-white/5 shadow-soft"
        >
            {/* Color header stripe */}
            <div className={`h-1.5 bg-gradient-to-r ${kind.tone}`} />
            <div className="p-4 flex items-center gap-3">
                <div className={`w-11 h-11 rounded-2xl grid place-items-center flex-shrink-0 text-xl bg-gradient-to-br ${kind.tone} text-white shadow-soft`}>
                    <span>{kind.emoji}</span>
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-semibold text-paw-ink dark:text-paw-bone truncate">{doc.name}</p>
                    <p className="text-[11px] text-paw-ink2 dark:text-paw-bone/60 flex items-center gap-1.5">
                        <span>{t(kind.labelKey)}</span>
                        {doc.uploaded_at && <span>· {new Date(doc.uploaded_at).toLocaleDateString()}</span>}
                    </p>
                </div>
                <a href={fileUrl(doc.file_path)} target="_blank" rel="noreferrer"
                    data-testid={`safety-doc-view-${doc.id}`}
                    className="text-xs font-semibold text-paw-forest dark:text-paw-terra hover:underline flex-shrink-0">
                    {t("safety.view")}
                </a>
                <button type="button" onClick={togglePublic} disabled={busy}
                    data-testid={`safety-doc-toggle-${doc.id}`}
                    className={`w-9 h-9 rounded-full grid place-items-center transition flex-shrink-0 ${doc.is_public
                        ? "bg-paw-forest/10 text-paw-forest dark:bg-paw-terra/20 dark:text-paw-terra"
                        : "text-paw-ink2 dark:text-paw-bone/50 hover:bg-paw-muted dark:hover:bg-white/5"}`}
                    title={t(doc.is_public ? "safety.tt_public" : "safety.tt_private")}>
                    {doc.is_public ? <Globe size={16} weight="fill" /> : <LockKey size={16} weight="fill" />}
                </button>
                <button type="button" onClick={remove} disabled={busy}
                    data-testid={`safety-doc-remove-${doc.id}`}
                    className="w-9 h-9 rounded-full text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 grid place-items-center transition flex-shrink-0">
                    <Trash size={16} />
                </button>
            </div>
        </article>
    );
}
